import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, Calculator, Landmark, Users, User, Edit, Search, Mail, Phone, CreditCard, Maximize2, Calendar } from 'lucide-react';
import { formatAddressTwoLines } from '../../utils/formatters';

// Full detail row for expanded view
const DetailRow = ({ label, value, icon: Icon, iconColor, mono = false }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-1.5">
      {Icon && <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${iconColor || 'text-slate-400'}`} />}
      <span className="text-slate-500 text-sm min-w-[100px] flex-shrink-0">{label}</span>
      <span className={`text-slate-800 text-sm font-medium break-words ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
};

// Compact two-column grid for small data points (card view) - NO truncation
const CompactGrid = ({ items }) => {
  const validItems = items.filter(item => item.value);
  if (validItems.length === 0) return null;
  
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-2 mt-2 border-t border-slate-100">
      {validItems.map((item, idx) => (
        <div key={idx} className="min-w-0">
          <dt className="text-slate-500 text-[10px] leading-tight">{item.label}</dt>
          <dd className="text-slate-700 text-xs font-medium font-mono truncate">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

// Full grid for expanded view - card style
const ExpandedGrid = ({ items, color = 'slate' }) => {
  const validItems = items.filter(item => item.value);
  if (validItems.length === 0) return null;
  
  const colorClasses = {
    slate: 'bg-slate-50 border-slate-200',
    blue: 'bg-blue-50/50 border-blue-100',
    purple: 'bg-purple-50/50 border-purple-100',
    teal: 'bg-teal-50/50 border-teal-100',
    rose: 'bg-rose-50/50 border-rose-100',
    orange: 'bg-orange-50/50 border-orange-100',
  };
  
  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color] || colorClasses.slate}`}>
      <dl className="grid grid-cols-2 gap-x-8 gap-y-3">
        {validItems.map((item, idx) => (
          <div key={idx}>
            <dt className="text-xs text-slate-500 uppercase tracking-wide mb-0.5">{item.label}</dt>
            <dd className="text-slate-800 font-semibold">{item.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// Contact card for expanded view
const ContactCard = ({ name, email, phone, color = 'slate' }) => {
  if (!name && !email && !phone) return null;
  
  const iconColors = {
    slate: 'text-slate-500',
    purple: 'text-purple-500',
    teal: 'text-teal-500',
    rose: 'text-rose-500',
    orange: 'text-orange-500',
  };
  
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Contact</p>
      {name && <p className="font-semibold text-slate-800 mb-2">{name}</p>}
      <div className="space-y-2">
        {email && (
          <a href={`mailto:${email}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors">
            <Mail className={`w-4 h-4 ${iconColors[color]}`} />
            <span className="text-sm">{email}</span>
          </a>
        )}
        {phone && (
          <a href={`tel:${phone.replace(/\D/g, '')}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors">
            <Phone className={`w-4 h-4 ${iconColors[color]}`} />
            <span className="text-sm">{phone}</span>
          </a>
        )}
      </div>
    </div>
  );
};

export const InfoPanels = ({ 
  llc, 
  canEditLLC, 
  onEditProperty, 
  onEditAccountant, 
  onEditBank, 
  onEditLender,
  onEditTenant,
  onPropertyLookup 
}) => {
  const [expandedPanel, setExpandedPanel] = useState(null);
  
  if (!llc) return null;

  const openPanel = (panelName) => setExpandedPanel(panelName);
  const closePanel = () => setExpandedPanel(null);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Property Information */}
        <Card className="border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => openPanel('property')} data-testid="property-info-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-slate-100 to-slate-50">
            <CardTitle className="font-serif text-sm flex items-center gap-2 whitespace-nowrap">
              <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-blue-600" />
              </div>
              Property Info
            </CardTitle>
            <div className="flex items-center gap-1 flex-shrink-0">
              {llc.property_address && (
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onPropertyLookup(); }} title="Property Lookup" className="h-7 w-7 hover:bg-blue-100">
                  <Search className="w-3.5 h-3.5 text-blue-600" />
                </Button>
              )}
              {canEditLLC && (
                <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditProperty(); }} className="h-7 w-7 hover:bg-slate-200">
                  <Edit className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-3 pb-4">
            {llc.property_address ? (
              <div className="space-y-1">
                {(() => {
                  const formatted = formatAddressTwoLines(llc.property_address);
                  if (formatted && formatted.line2) {
                    return (
                      <div className="space-y-0.5">
                        <p className="font-medium text-slate-800 text-sm leading-snug">{formatted.line1}</p>
                        <p className="text-slate-600 text-sm">{formatted.line2}</p>
                      </div>
                    );
                  }
                  return <p className="font-medium text-slate-800 text-sm">{llc.property_address}</p>;
                })()}
                {llc.property_type && (
                  <div className="pt-1">
                    <Badge variant="outline" className={`text-xs ${llc.property_type === 'commercial' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-green-100 text-green-700 border-green-200'}`}>
                      {llc.property_type === 'commercial' ? 'Commercial' : 'Residential'}
                    </Badge>
                  </div>
                )}
                <CompactGrid items={[
                  { label: 'Built', value: llc.property_year_built },
                  { label: 'Acquired', value: llc.property_year_acquired },
                  { label: 'Size', value: llc.property_size_sqft ? `${llc.property_size_sqft.toLocaleString()} sqft` : null },
                  { label: 'Acreage', value: llc.property_acreage },
                ]} />
                {llc.property_description && (
                  <p className="text-slate-500 pt-2 text-xs leading-relaxed border-t border-slate-100 mt-2">{llc.property_description}</p>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No property information</p>
            )}
            <div className="flex justify-end mt-2">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Bank Information */}
        <Card className="border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => openPanel('bank')} data-testid="bank-info-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-slate-100 to-slate-50">
            <CardTitle className="font-serif text-sm flex items-center gap-2 whitespace-nowrap">
              <div className="w-7 h-7 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Landmark className="w-4 h-4 text-teal-600" />
              </div>
              Bank Info
            </CardTitle>
            {canEditLLC && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditBank(); }} className="h-7 w-7 hover:bg-slate-200">
                <Edit className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-3 pb-4">
            {llc.bank_name ? (
              <div className="space-y-2">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{llc.bank_name}</p>
                  {llc.bank_address && (
                    (() => {
                      const formatted = formatAddressTwoLines(llc.bank_address);
                      if (formatted && formatted.line2) {
                        return (
                          <div className="space-y-0.5 mt-1">
                            <p className="text-slate-500 text-xs leading-relaxed">{formatted.line1}</p>
                            <p className="text-slate-500 text-xs">{formatted.line2}</p>
                          </div>
                        );
                      }
                      return <p className="text-slate-500 text-xs leading-relaxed">{llc.bank_address}</p>;
                    })()
                  )}
                </div>
                {llc.bank_account_name && <p className="text-slate-600 text-sm">{llc.bank_account_name}</p>}
                {llc.bank_account_number && (
                  <p className="text-slate-600 text-sm font-mono">Acct: ****{llc.bank_account_number.slice(-4)}</p>
                )}
                <CompactGrid items={[
                  { label: 'Route In', value: llc.bank_routing_inbound },
                  { label: 'Route Out', value: llc.bank_routing_outbound },
                ]} />
                {llc.bank_phone && (
                  <p className="flex items-center gap-2 text-slate-600 text-sm pt-2 border-t border-slate-100">
                    <Phone className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" /> 
                    {llc.bank_phone}
                  </p>
                )}
                {llc.bank_officer && (
                  <p className="flex items-center gap-2 text-slate-600 text-sm pt-1">
                    <User className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" /> 
                    {llc.bank_officer}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No bank information</p>
            )}
            <div className="flex justify-end mt-2">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Third Party Liabilities - Lender Information */}
        <Card className="border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => openPanel('lender')} data-testid="lender-info-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-slate-100 to-slate-50">
            <CardTitle className="font-serif text-sm flex items-center gap-2 whitespace-nowrap">
              <div className="w-7 h-7 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-4 h-4 text-rose-600" />
              </div>
              Lender Info
            </CardTitle>
            {canEditLLC && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditLender(); }} className="h-7 w-7 hover:bg-slate-200">
                <Edit className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-3 pb-4">
            {llc.lender_name ? (
              <div className="space-y-2">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{llc.lender_name}</p>
                  {llc.lender_address && (
                    (() => {
                      const formatted = formatAddressTwoLines(llc.lender_address);
                      if (formatted && formatted.line2) {
                        return (
                          <div className="space-y-0.5 mt-1">
                            <p className="text-slate-500 text-xs leading-relaxed">{formatted.line1}</p>
                            <p className="text-slate-500 text-xs">{formatted.line2}</p>
                          </div>
                        );
                      }
                      return <p className="text-slate-500 text-xs leading-relaxed">{llc.lender_address}</p>;
                    })()
                  )}
                </div>
                {llc.lender_account_number && (
                  <p className="text-slate-600 text-sm font-mono">Loan #: {llc.lender_account_number}</p>
                )}
                <CompactGrid items={[
                  { label: 'Rate', value: llc.lender_interest_rate ? `${llc.lender_interest_rate}%` : null },
                  { label: 'Maturity', value: llc.lender_maturity_date },
                ]} />
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No lender information</p>
            )}
            <div className="flex justify-end mt-2">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Accountant Information */}
        <Card className="border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => openPanel('accountant')} data-testid="accountant-info-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-slate-100 to-slate-50">
            <CardTitle className="font-serif text-sm flex items-center gap-2 whitespace-nowrap">
              <div className="w-7 h-7 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calculator className="w-4 h-4 text-purple-600" />
              </div>
              Accountant Info
            </CardTitle>
            {canEditLLC && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditAccountant(); }} className="h-7 w-7 hover:bg-slate-200">
                <Edit className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-3 pb-4">
            {llc.accountant_name ? (
              <div className="space-y-2">
                <div>
                  <p className="font-medium text-slate-800 text-sm">{llc.accountant_name}</p>
                  {llc.accountant_firm && <p className="text-slate-500 text-sm">{llc.accountant_firm}</p>}
                </div>
                {llc.accountant_address && (
                  (() => {
                    const formatted = formatAddressTwoLines(llc.accountant_address);
                    if (formatted && formatted.line2) {
                      return (
                        <div className="space-y-0.5">
                          <p className="text-slate-500 text-xs leading-relaxed">{formatted.line1}</p>
                          <p className="text-slate-500 text-xs">{formatted.line2}</p>
                        </div>
                      );
                    }
                    return <p className="text-slate-500 text-xs leading-relaxed">{llc.accountant_address}</p>;
                  })()
                )}
                {(llc.accountant_email || llc.accountant_phone) && (
                  <div className="space-y-1 pt-2 border-t border-slate-100">
                    {llc.accountant_phone && (
                      <p className="flex items-center gap-2 text-slate-600 text-sm">
                        <Phone className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" /> 
                        {llc.accountant_phone}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No accountant information</p>
            )}
            <div className="flex justify-end mt-2">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        {/* Tenant Information */}
        <Card className="border-slate-200 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => openPanel('tenant')} data-testid="tenant-info-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-slate-100 to-slate-50">
            <CardTitle className="font-serif text-sm flex items-center gap-2 whitespace-nowrap">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-orange-600" />
              </div>
              Tenant Info
            </CardTitle>
            {canEditLLC && (
              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onEditTenant(); }} className="h-7 w-7 hover:bg-slate-200">
                <Edit className="w-3.5 h-3.5" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-3 pb-4">
            {llc.tenant_name ? (
              <div className="space-y-2">
                <p className="font-medium text-slate-800 text-sm">{llc.tenant_name}</p>
                {(llc.lease_sqft || llc.lease_rate_per_sqft) && (
                  <div className="flex gap-4 pt-1">
                    {llc.lease_sqft && (
                      <div className="flex-1 text-center bg-slate-50 rounded-lg py-2 px-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Size</p>
                        <p className="font-semibold text-slate-800 text-sm">{llc.lease_sqft.toLocaleString()} <span className="text-slate-500 font-normal">sqft</span></p>
                      </div>
                    )}
                    {llc.lease_rate_per_sqft && (
                      <div className="flex-1 text-center bg-slate-50 rounded-lg py-2 px-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Rate</p>
                        <p className="font-semibold text-slate-800 text-sm">${llc.lease_rate_per_sqft.toFixed(2)}<span className="text-slate-500 font-normal">/sqft</span></p>
                      </div>
                    )}
                  </div>
                )}
                {llc.lease_expiration_date && (
                  <div className="flex items-center gap-2 text-sm pt-2 border-t border-slate-100">
                    <Calendar className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-slate-500">Expires:</span>
                    <span className="font-medium text-slate-700">{new Date(llc.lease_expiration_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-400 text-sm italic">No tenant information</p>
            )}
            <div className="flex justify-end mt-2">
              <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Property Info Popup */}
      <Dialog open={expandedPanel === 'property'} onOpenChange={closePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <div className="w-11 h-11 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center shadow-sm">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <span className="font-semibold">Property Information</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {llc.property_address ? (
              <>
                <div className="rounded-lg border border-blue-100 bg-blue-50/30 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Address</p>
                  {(() => {
                    const formatted = formatAddressTwoLines(llc.property_address);
                    if (formatted && formatted.line2) {
                      return (
                        <div className="space-y-0.5">
                          <p className="font-semibold text-slate-800 text-lg">{formatted.line1}</p>
                          <p className="text-slate-600">{formatted.line2}</p>
                        </div>
                      );
                    }
                    return <p className="font-semibold text-slate-800 text-lg">{llc.property_address}</p>;
                  })()}
                  {llc.property_type && (
                    <div className="mt-3">
                      <Badge variant="outline" className={`${llc.property_type === 'commercial' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-green-100 text-green-700 border-green-300'} px-3 py-1`}>
                        {llc.property_type === 'commercial' ? 'Commercial' : 'Residential'}
                      </Badge>
                    </div>
                  )}
                </div>
                <ExpandedGrid color="blue" items={[
                  { label: 'Year Built', value: llc.property_year_built },
                  { label: 'Year Acquired', value: llc.property_year_acquired },
                  { label: 'Size', value: llc.property_size_sqft ? `${llc.property_size_sqft.toLocaleString()} sqft` : null },
                  { label: 'Acreage', value: llc.property_acreage },
                ]} />
                {llc.property_description && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Description</p>
                    <p className="text-slate-700 text-sm leading-relaxed">{llc.property_description}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No property information available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Accountant Info Popup */}
      <Dialog open={expandedPanel === 'accountant'} onOpenChange={closePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <div className="w-11 h-11 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center shadow-sm">
                <Calculator className="w-5 h-5 text-purple-600" />
              </div>
              <span className="font-semibold">Accountant Information</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {llc.accountant_name ? (
              <>
                <div className="rounded-lg border border-purple-100 bg-purple-50/30 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Name</p>
                  <p className="font-semibold text-slate-800 text-lg">{llc.accountant_name}</p>
                  {llc.accountant_firm && (
                    <p className="text-slate-600 mt-1">{llc.accountant_firm}</p>
                  )}
                </div>
                {llc.accountant_address && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Address</p>
                    {(() => {
                      const formatted = formatAddressTwoLines(llc.accountant_address);
                      if (formatted && formatted.line2) {
                        return (
                          <div className="space-y-0.5">
                            <p className="text-slate-700">{formatted.line1}</p>
                            <p className="text-slate-600">{formatted.line2}</p>
                          </div>
                        );
                      }
                      return <p className="text-slate-700">{llc.accountant_address}</p>;
                    })()}
                  </div>
                )}
                <ContactCard 
                  email={llc.accountant_email} 
                  phone={llc.accountant_phone}
                  color="purple"
                />
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Calculator className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No accountant information available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Info Popup */}
      <Dialog open={expandedPanel === 'bank'} onOpenChange={closePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <div className="w-11 h-11 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center shadow-sm">
                <Landmark className="w-5 h-5 text-teal-600" />
              </div>
              <span className="font-semibold">Bank Information</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {llc.bank_name ? (
              <>
                <div className="rounded-lg border border-teal-100 bg-teal-50/30 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Bank</p>
                  <p className="font-semibold text-slate-800 text-lg">{llc.bank_name}</p>
                  {llc.bank_address && (
                    (() => {
                      const formatted = formatAddressTwoLines(llc.bank_address);
                      if (formatted && formatted.line2) {
                        return (
                          <div className="space-y-0.5 mt-1">
                            <p className="text-slate-600 text-sm">{formatted.line1}</p>
                            <p className="text-slate-600 text-sm">{formatted.line2}</p>
                          </div>
                        );
                      }
                      return <p className="text-slate-600 mt-1 text-sm">{llc.bank_address}</p>;
                    })()
                  )}
                </div>
                {llc.bank_account_name && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Account Name</p>
                    <p className="text-slate-800 font-medium">{llc.bank_account_name}</p>
                  </div>
                )}
                <ExpandedGrid color="teal" items={[
                  { label: 'Account #', value: llc.bank_account_number ? `****${llc.bank_account_number.slice(-4)}` : null },
                  { label: 'Routing (In)', value: llc.bank_routing_inbound },
                  { label: 'Routing (Out)', value: llc.bank_routing_outbound },
                ]} />
                <ContactCard 
                  name={llc.bank_officer}
                  email={llc.bank_email} 
                  phone={llc.bank_phone}
                  color="teal"
                />
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Landmark className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No bank information available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lender Info Popup */}
      <Dialog open={expandedPanel === 'lender'} onOpenChange={closePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <div className="w-11 h-11 bg-gradient-to-br from-rose-100 to-rose-200 rounded-xl flex items-center justify-center shadow-sm">
                <CreditCard className="w-5 h-5 text-rose-600" />
              </div>
              <span className="font-semibold">Lender Information</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {llc.lender_name ? (
              <>
                <div className="rounded-lg border border-rose-100 bg-rose-50/30 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Lender</p>
                  <p className="font-semibold text-slate-800 text-lg">{llc.lender_name}</p>
                  {llc.lender_address && (
                    (() => {
                      const formatted = formatAddressTwoLines(llc.lender_address);
                      if (formatted && formatted.line2) {
                        return (
                          <div className="space-y-0.5 mt-1">
                            <p className="text-slate-600 text-sm">{formatted.line1}</p>
                            <p className="text-slate-600 text-sm">{formatted.line2}</p>
                          </div>
                        );
                      }
                      return <p className="text-slate-600 mt-1 text-sm">{llc.lender_address}</p>;
                    })()
                  )}
                </div>
                {(llc.lender_account_number || llc.lender_original_amount) && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <div className="grid grid-cols-2 gap-4">
                      {llc.lender_account_number && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Loan #</p>
                          <p className="text-slate-800 font-mono font-medium">{llc.lender_account_number}</p>
                        </div>
                      )}
                      {llc.lender_original_amount && (
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Original Amount</p>
                          <p className="text-slate-800 font-semibold">${llc.lender_original_amount.toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <ExpandedGrid color="rose" items={[
                  { label: 'Interest Rate', value: llc.lender_interest_rate ? `${llc.lender_interest_rate}%` : null },
                  { label: 'Maturity Date', value: llc.lender_maturity_date },
                ]} />
                <ContactCard 
                  name={llc.lender_contact_name}
                  email={llc.lender_email} 
                  phone={llc.lender_phone}
                  color="rose"
                />
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No lender information available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenant Info Popup */}
      <Dialog open={expandedPanel === 'tenant'} onOpenChange={closePanel}>
        <DialogContent className="max-w-md">
          <DialogHeader className="pb-4 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-3 text-lg">
              <div className="w-11 h-11 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center shadow-sm">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <span className="font-semibold">Tenant Information</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {llc.tenant_name ? (
              <>
                <div className="rounded-lg border border-orange-100 bg-orange-50/30 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Tenant</p>
                  <p className="font-semibold text-slate-800 text-lg">{llc.tenant_name}</p>
                </div>
                <ExpandedGrid color="orange" items={[
                  { label: 'Leased Size', value: llc.lease_sqft ? `${llc.lease_sqft.toLocaleString()} sqft` : null },
                  { label: 'Rate / sqft', value: llc.lease_rate_per_sqft ? `$${llc.lease_rate_per_sqft.toFixed(2)}` : null },
                ]} />
                {(llc.lease_start_date || llc.lease_expiration_date) && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Lease Period</p>
                    <div className="grid grid-cols-2 gap-4">
                      {llc.lease_start_date && (
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">Start Date</p>
                          <p className="text-slate-800 font-semibold">
                            {new Date(llc.lease_start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                      {llc.lease_expiration_date && (
                        <div>
                          <p className="text-xs text-slate-400 mb-0.5">Expiration Date</p>
                          <p className="text-slate-800 font-semibold">
                            {new Date(llc.lease_expiration_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No tenant information available</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
